import { Badge, Skeleton } from "@veritrellis/ui";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import {
  auditCategoryAccent,
  auditEventBadgeVariant,
  categorizeAuditEvent,
  formatAuditEventTitle,
  formatRelativeTime,
  type AuditEvent
} from "../../lib/auditPresentation.js";
import { actorBadgeVariant, formatActorLabel } from "../../lib/requestPresentation.js";

type AuditEventTimelineProps = {
  events: AuditEvent[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  loading?: boolean;
};

export const AuditEventTimeline = ({
  events,
  selectedId,
  onSelect,
  loading = false
}: AuditEventTimelineProps) => {
  if (loading) {
    return (
      <div className="admin-audit-timeline-loading">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Skeleton key={i} style={{ height: 64, width: "100%", marginBottom: 8 }} />
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return null;
  }

  return (
    <div className="admin-audit-timeline" role="list">
      {events.map((event) => {
        const category = categorizeAuditEvent(event.event_type);
        const accent = auditCategoryAccent[category];
        const selected = selectedId === event.id;

        return (
          <article key={event.id} role="listitem">
            <button
              type="button"
              className={`admin-audit-event-row${selected ? " admin-audit-event-row-selected" : ""}`}
              onClick={() => onSelect(event.id)}
              aria-pressed={selected}
            >
              <span
                className="admin-audit-event-marker"
                style={{ background: accent }}
                aria-hidden
              />
              <span className="admin-audit-event-body">
                <span className="admin-audit-event-top">
                  <span className="admin-audit-event-title">
                    {formatAuditEventTitle(event.event_type)}
                  </span>
                  <time
                    className="admin-audit-event-time"
                    dateTime={event.created_at}
                    title={event.created_at}
                  >
                    {formatRelativeTime(event.created_at)}
                  </time>
                </span>
                <span className="admin-audit-event-meta">
                  <Badge
                    variant={auditEventBadgeVariant(event.event_type)}
                    className="normal-case"
                    style={{ fontSize: 10 }}
                  >
                    {event.event_type}
                  </Badge>
                  <Badge variant={actorBadgeVariant(event.actor.type)} className="normal-case">
                    {formatActorLabel(event.actor)}
                  </Badge>
                  {event.request_id != null ? (
                    <Link
                      to={`/requests/${event.request_id}`}
                      className="admin-audit-event-request-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {event.request_id.slice(0, 10)}…
                    </Link>
                  ) : null}
                </span>
              </span>
              <ChevronRight
                style={{ width: 16, height: 16, flexShrink: 0, opacity: 0.35 }}
                aria-hidden
              />
            </button>
          </article>
        );
      })}
    </div>
  );
};
